import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Loader, Sparkles, BookOpen, Calendar, Eye, EyeOff, CheckCircle, Globe } from 'lucide-react';
import api from '../../utils/api';

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";
const blank = { title:'', excerpt:'', content:'', category:'Tips & Advice', author:'Apex Team', date: new Date().toISOString().split('T')[0], image:'', published:false };

// ── Tow Truck Animation ────────────────────────────────────────────────────
function TowTruckAnimation({ message = 'Writing your blog post...' }) {
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-apex-blue-100 my-4">
      <style>{`
        @keyframes blogTruck { 0% { left: -120px; } 100% { left: 110%; } }
        .blog-truck { position:absolute; bottom:8px; left:-120px; animation:blogTruck 2.2s linear infinite; font-size:34px; white-space:nowrap; line-height:1; }
      `}</style>
      <div style={{ position:'relative', height:110, overflow:'hidden', background:'linear-gradient(180deg,#bfdbfe 0%,#dbeafe 55%,#374151 55%,#1f2937 100%)' }}>
        <span style={{ position:'absolute', left:'8%',  top:8,  fontSize:22 }}>☁️</span>
        <span style={{ position:'absolute', left:'50%', top:5,  fontSize:26 }}>☁️</span>
        <span style={{ position:'absolute', right:'12%',top:12, fontSize:18 }}>☁️</span>
        <div style={{ position:'absolute', bottom:16, left:0, right:0, display:'flex', gap:12, padding:'0 8px', overflow:'hidden' }}>
          {Array(14).fill(0).map((_,i) => <div key={i} style={{ width:28, height:4, background:'#F5C400', borderRadius:2, flexShrink:0 }} />)}
        </div>
        <div className="blog-truck">🚛📝</div>
      </div>
      <div className="bg-white border-t-2 border-apex-blue-100 text-center py-3">
        <p className="text-apex-blue-700 font-black text-sm">{message}</p>
        <p className="text-gray-400 text-xs mt-0.5">Powered by AI · Usually takes 5–10 seconds</p>
      </div>
    </div>
  );
}

// ── 20 Pre-written Brisbane Towing Blog Templates ─────────────────────────
const TEMPLATES = [
  {
    title: '5 Signs You Need Emergency Towing Right Now',
    category: 'Emergency Tips',
    excerpt: 'Not every breakdown requires emergency towing — but some situations are too dangerous to wait. Here are 5 clear signs you need to call immediately.',
    content: `When your car stops working, it can be hard to know whether you need emergency towing or can wait for a scheduled pickup. In Brisbane's busy traffic, making the wrong call can be dangerous.

**1. Smoke or Steam Coming from the Engine**
If you see smoke or steam from under the bonnet, pull over immediately and turn off the engine. This could be an overheating engine or oil leak — driving further will cause major damage. Call us straight away.

**2. Brake Failure**
If your brakes feel spongy, or the pedal goes to the floor, stop the car safely using your handbrake and call for emergency towing. Never drive with compromised brakes.

**3. You're on a Motorway or Busy Road**
Being broken down on the M1, Pacific Motorway, or any major Brisbane road is extremely dangerous. Get onto the shoulder, turn on hazard lights, and call immediately. We'll prioritise motorway breakdowns.

**4. Fluid Leaking Under Your Car**
Red fluid = transmission. Green/orange = coolant. Dark brown = engine oil. Any of these mean your car should not be driven. Photograph the fluid and call us.

**5. Your Car Won't Start After Multiple Attempts**
If your car repeatedly fails to start, you risk damaging the starter motor or draining the battery completely. Don't keep trying — call Apex Towing and we'll sort it fast.

**Brisbane's Traffic Makes Quick Response Critical**
Our drivers know every Brisbane suburb — from the CBD to the outer suburbs — and can typically reach you within 30 minutes. Save our number now so you're never stuck without help.`,
  },
  {
    title: 'What Happens to Your Car After Free Removal?',
    category: 'Car Removal',
    excerpt: 'Ever wondered what actually happens to your old car once we take it away? Here\'s the full story from pickup to responsible recycling.',
    content: `When you book a free car removal with Apex Towing, the process doesn't end when we drive away from your driveway. Here's exactly what happens next.

**Step 1: Assessment and Valuation**
Once your vehicle arrives at our yard, our team performs a full assessment. We check the body, engine, transmission, tyres, and all reusable parts. This determines the final value of the car.

**Step 2: Useful Parts Are Removed and Resold**
Working parts — engines, gearboxes, doors, mirrors, wheels — are carefully removed and resold to mechanics and panel beaters across Brisbane. This keeps good parts in circulation and out of landfill.

**Step 3: Fluids Are Safely Drained**
Engine oil, coolant, transmission fluid, brake fluid, and air conditioning refrigerant are all professionally drained and disposed of according to Queensland EPA regulations. None of this goes down the drain.

**Step 4: The Metal Is Recycled**
The remaining steel and aluminium shell is compressed and sent to a metal recycling facility. Your old Corolla could end up as a new washing machine or steel beam — metal recycling is 100% circular.

**Step 5: Paperwork Is Finalised**
We handle all transfer paperwork and notify the Queensland Department of Transport. This is important — it protects you from any future liability (like fines or tolls) if someone else uses your old plates.

**Why It Matters**
Choosing licensed, responsible car removal isn't just convenient — it's the environmentally and legally correct choice. At Apex Towing, we're fully licensed and take our environmental responsibilities seriously.

Have a car sitting in your driveway? Call us today for free removal across all Brisbane suburbs.`,
  },
  {
    title: 'Cash for Cars in Brisbane: How to Get the Best Price',
    category: 'Cash for Cars',
    excerpt: 'Getting cash for your old car in Brisbane is easier than you think — but a few simple steps can significantly increase the offer you receive.',
    content: `Whether your car is old, damaged, or simply unwanted, you can get real cash for it in Brisbane today. Here's how to maximise your payout.

**Know Your Car's Value Before You Call**
Do some basic research first. Check how similar cars are selling on Gumtree and CarsGuide. This gives you a realistic expectation before you speak to any buyer. Remember — cash buyers pay less than private sale prices, but the convenience and speed make up for it.

**Gather Your Paperwork**
Having your registration papers, service history, and any receipts for recent work done significantly increases buyer confidence — and often the offer. A car with a full service history is worth more, even for scrap.

**Be Honest About the Condition**
Trying to hide damage or mechanical problems will only lead to a reduced offer when the buyer inspects. Be upfront about the condition — experienced buyers will find issues anyway, and honesty builds trust.

**What Increases the Offer?**
- Recently serviced engine
- Working air conditioning
- Newer tyres
- Full set of keys
- Clean interior
- Low kilometres for the age
- Popular makes: Toyota, Mazda, Holden, Ford

**What We Pay For (Even Damaged Cars)**
You don't need a running car to get cash. We buy:
- Cars that won't start
- Accident-damaged vehicles
- Flood-damaged cars
- High-mileage vehicles
- Unregistered cars

**Same Day Payment**
At Apex Towing, we offer same-day cash payment across all Brisbane suburbs. Call us, get an instant estimate, we come to inspect, and you walk away with cash in hand.`,
  },
  {
    title: 'Brisbane Motorway Breakdown Guide: What to Do Step by Step',
    category: 'Emergency Tips',
    excerpt: 'Breaking down on the M1 or Bruce Highway is one of the most stressful experiences a driver can face. Here\'s exactly what to do to stay safe.',
    content: `Breaking down on a Brisbane motorway is dangerous. High speeds, limited visibility, and heavy traffic create real risks. Follow these steps exactly.

**Step 1: Get Off the Road If Possible**
If your car is still moving, head for the nearest exit or emergency stopping lane. Do not stop in a live traffic lane if you can avoid it.

**Step 2: Turn On Hazard Lights Immediately**
The moment you feel something is wrong, activate your hazard lights. This alerts other drivers even before you've stopped.

**Step 3: Get to the Left Shoulder**
Aim for the far left side of the road — as far from traffic as possible. If on a narrow section, angle your car slightly away from traffic to give yourself a protected zone.

**Step 4: Exit the Vehicle on the Safe Side**
Exit through the passenger door if possible, keeping you away from passing vehicles. Get behind the guard rail and stay there.

**Step 5: Set Up Warning Triangles or Flares**
If you have them, place warning triangles at least 100m behind your vehicle. This is especially important at night or in poor weather.

**Step 6: Call for Help**
Call Apex Towing on our emergency line. Tell us: your location (nearest kilometre marker or exit), direction of travel, and make/model of your car. We prioritise motorway callouts.

**Key Brisbane Motorways We Cover**
- M1 Pacific Motorway
- Bruce Highway (A1)
- Ipswich Motorway
- Gateway Motorway (M7)
- Logan Motorway (M6)
- Western Arterial

**Never Push Your Car on a Motorway**
It's extremely dangerous and illegal. Wait for professional assistance. Stay behind the barrier at all times.`,
  },
  {
    title: 'Queensland Towing Laws Every Driver Should Know',
    category: 'Legal & Safety',
    excerpt: 'Queensland has specific rules about towing, roadside assistance, and car removal. Knowing your rights can save you money and stress.',
    content: `Understanding towing regulations in Queensland protects you from being overcharged and ensures your vehicle is handled properly. Here's what every Brisbane driver should know.

**The Tow Truck Act Queensland**
Queensland has strict regulations under the Tow Truck Act 1973 governing who can tow your vehicle and how much they can charge. Always ask for a copy of the invoice before your car is moved.

**At-Fault Towing After Accidents**
If you're involved in an accident and your car needs towing, you do NOT have to use the tow truck that arrives first. You have the right to choose your own towing company. Some operators work on commission with particular towing companies — always choose independently.

**Abandoning a Vehicle Is an Offence**
Leaving a vehicle on public property (including roadsides) for more than 48 hours without a reasonable excuse can result in a fine. If your car breaks down, arrange towing promptly.

**Private Property Towing**
Vehicles on private property (shopping centres, driveways, etc.) can only be towed if clear signage warns of towing and the property owner authorises it. Disputes can be taken to QCAT.

**What Towing Companies Must Provide**
Licensed towing operators must:
- Provide a written quote or estimate before towing
- Give you an itemised invoice
- Tell you where your vehicle is being taken
- Hold your vehicle safely without charging excessive storage fees

**Choosing a Licensed Operator**
Always use a licensed towing operator. Apex Towing is fully licensed under Queensland regulations, fully insured, and transparent with all pricing.

**Free Car Removal Is Legal**
Offering free car removal is completely legal — it's a commercial arrangement where the towing company pays for the right to dismantle and recycle the vehicle.`,
  },
  {
    title: 'Brisbane Flood Season: Protecting Your Vehicle',
    category: 'Weather & Safety',
    excerpt: 'Brisbane\'s wet season brings real flood risk to hundreds of thousands of vehicles. Here\'s how to protect yours — and what to do if it\'s already too late.',
    content: `Brisbane has a history of devastating floods. The 2011 and 2022 flood events wrote off thousands of vehicles. As flood risk becomes more frequent, knowing how to protect — and recover — your vehicle is critical.

**Understanding Flood Risk Zones in Brisbane**
Low-lying suburbs near the Brisbane River, Kedron Brook, and Oxley Creek are most at risk. Suburbs like Rocklea, Moorooka, Sherwood, Chelmer, and Graceville have flooded multiple times. Check Brisbane City Council's Flood Map before parking in unfamiliar areas during heavy rain.

**What to Do Before Floodwater Arrives**
- Move your vehicle to higher ground immediately — don't wait
- If your suburb has a flood alert, act within the first hour
- Park on the highest available point nearby (uphill, multi-level car parks)
- Remove personal items from the car in case it's written off

**If Your Car Gets Wet**
- DO NOT start the car if water has entered the engine bay
- Call your insurer immediately and document with photos
- Arrange professional assessment before attempting to start

**When a Car Is Flood-Written Off**
If floodwater has entered the cabin, engine, or electrical system, your car may be declared a total loss. We can remove flood-damaged vehicles quickly, freeing up your driveway and helping you move on.

**We Take Flood-Damaged Cars**
Apex Towing accepts flood-damaged vehicles in any condition — running or not, registered or written off. We'll pay cash and remove it for free. This is faster than dealing with insurers or salvage yards alone.

**Warning: Flood Cars Sold Privately**
Be cautious when buying second-hand cars in Brisbane after flood events. Flood damage is often cosmetically repaired and resold. Always get a PPSR check and mechanical inspection.`,
  },
  {
    title: 'Selling Your Car vs Free Removal: Which Makes More Sense?',
    category: 'Cash for Cars',
    excerpt: 'Should you sell your old Brisbane car privately or use a free removal service? The answer depends on a few key factors most people overlook.',
    content: `Every Brisbane car owner with an old or unwanted vehicle faces the same question: try to sell it, or just get rid of it? Here's an honest breakdown.

**Private Sale: The Pros**
The biggest advantage is price. A car sold privately typically fetches more than a trade-in or cash-for-cars offer. If your car runs well, has low kilometres, and is in decent condition, private sale can be worth the effort.

**Private Sale: The Real Costs Most People Forget**
- Advertising (Gumtree, CarsGuide): $0–$100
- Time spent answering messages and no-shows: significant
- Test drives with strangers: risk factor
- Negotiation stress and lowball offers
- Waiting: can take weeks or months
- Roadworthy certificate: $150–$200 typically required

**When Free Car Removal Wins**
Free removal makes more sense when:
- Your car doesn't run or needs major repairs
- It's been sitting unregistered for a while
- You need money or space quickly
- The car's value is under $2,000 (private sale effort doesn't justify the reward)
- You don't have time to deal with tyre-kickers

**Cash for Cars: The Middle Ground**
Cash-for-cars services like ours pay you on the spot, same day. You don't have to advertise, meet strangers, or get a roadworthy. The tradeoff is a lower price than private sale — but for many Brisbane households, the time and stress savings are worth it.

**Our Recommendation**
If your car is worth over $5,000 and runs well: consider private sale.
If it's under $3,000, damaged, or not running: call us. You'll come out ahead when you factor in your time.

Get a free quote from Apex Towing today — no obligation, instant answer.`,
  },
  {
    title: '7 Car Maintenance Checks to Avoid a Brisbane Breakdown',
    category: 'Maintenance Tips',
    excerpt: 'Most Brisbane breakdowns are preventable. These 7 simple monthly checks take less than 15 minutes and could save you thousands.',
    content: `The leading causes of roadside breakdowns in Brisbane are all preventable with basic monthly maintenance. Here's your checklist.

**1. Tyre Pressure and Tread**
Under-inflated tyres are a major cause of blowouts, especially in Queensland's summer heat. Check pressure monthly (including the spare) and ensure tread depth is above 1.5mm. A 5c coin test works — if you can see the full 5c lettering, it's time for new tyres.

**2. Engine Coolant Level**
Brisbane summers are brutal on cooling systems. With the engine cold, check coolant is between MIN and MAX markers. Never open the coolant cap on a hot engine.

**3. Engine Oil**
Low oil = engine damage. Check monthly with the dipstick — wipe, reinsert, and check. If it's below the minimum, top up with the correct grade. Dark black oil that smells burnt means it's overdue for a change.

**4. Battery Terminals**
Queensland's heat accelerates battery degradation. Check terminals for corrosion (white powdery buildup) and clean with a mixture of bicarb soda and water. Most batteries last 3–4 years in Brisbane's climate.

**5. Brake Fluid**
Brake fluid absorbs moisture over time, reducing effectiveness. Check the reservoir is between MIN and MAX. Spongy brakes or a low pedal are urgent warning signs.

**6. Windscreen Wipers and Washer Fluid**
During Brisbane's wet season, wipers are critical safety equipment. Replace annually and always keep washer fluid topped up.

**7. Lights**
Walk around your car monthly and check all lights — headlights, indicators, brake lights, and reversing lights. Ask someone to stand behind the car while you press the brake pedal.

**When Something Doesn't Look Right**
If you're unsure about any of these checks, or your car feels different to drive, get it inspected. A $100 service visit is far cheaper than a $3,000 breakdown on the Pacific Motorway.

And if the worst does happen — Apex Towing has you covered across all Brisbane suburbs, 24/7.`,
  },
  {
    title: 'Heavy Machinery Transport in Brisbane: What You Need to Know',
    category: 'Industry & Business',
    excerpt: 'Moving heavy machinery around Brisbane requires specialised equipment, permits, and experience. Here\'s what construction and trade businesses need to understand.',
    content: `Whether you're moving excavators, bobcats, forklifts, or other heavy equipment across Brisbane, proper transport planning is essential. Here's a practical guide for Brisbane businesses.

**What Counts as Heavy Machinery?**
For transport purposes, heavy machinery typically means any equipment over 4.5 tonnes or wider than 2.5 metres. This includes:
- Excavators and mini-excavators
- Bobcats and skid steers
- Forklifts
- Road rollers and compactors
- Cranes and elevated work platforms
- Large generators

**Equipment Required**
Heavy machinery transport requires specific trailer types:
- Tilt trays (for lower-profile equipment)
- Float trailers (for taller or wider loads)
- Lowboy trailers (for the heaviest equipment)

At Apex Towing, we have equipment suited to loads up to 5 tonnes. For heavier loads, we can refer you to specialist heavy haulage operators.

**Permits in Queensland**
Oversized or overweight loads on Queensland roads require permits from the Department of Transport and Main Roads. These permits specify approved routes, travel times (often restricted to daylight hours), and may require pilot vehicles for very wide loads. We handle all permit applications on your behalf.

**Planning Your Move**
Allow at least 24–48 hours notice for machinery moves. We'll assess:
- Equipment dimensions and weight
- Origin and destination access (low bridges, soft ground, narrow driveways)
- Permit requirements
- Best route and time of day

**B2B Rates for Regular Clients**
Construction companies, plant hire businesses, and trade contractors with regular transport needs can access our B2B rate card with weekly invoicing and priority scheduling.

Contact Apex Towing to discuss your machinery transport needs across Brisbane and South East Queensland.`,
  },
  {
    title: 'Understanding Your Insurance When You Need Towing',
    category: 'Legal & Safety',
    excerpt: 'Many Brisbane drivers don\'t know what their car insurance actually covers when it comes to towing. Here\'s how to avoid costly surprises.',
    content: `Towing costs can add up quickly — and many Brisbane drivers are surprised to learn what their insurance does and doesn't cover. Let's break it down.

**Comprehensive vs Third Party**
Third party insurance does NOT cover towing costs for your own vehicle. You need comprehensive insurance for any towing coverage to apply.

**What Comprehensive Insurance Typically Covers**
Most comprehensive policies include towing to the nearest repairer after an accident. However, policies vary significantly. Key questions to ask your insurer:
- Is towing covered after a breakdown (not just accident)?
- How far will they cover towing?
- Do I have to use their preferred towing company?
- Is there a towing sub-limit?

**Roadside Assistance vs Insurance**
Many drivers confuse roadside assistance (like RACQ membership) with insurance. They're different:
- Roadside assistance covers breakdown situations (flat tyre, battery jump, lockout, fuel delivery, and short tows)
- Car insurance covers accidents and damage

**Your Right to Choose Your Tow Truck**
Queensland law gives you the right to choose your own towing company after an accident, regardless of what your insurer suggests. You are not obligated to use their preferred provider.

**When Insurance Doesn't Cover Towing**
You'll typically pay out of pocket for towing when:
- Your car breaks down (not accident-related) and you don't have roadside assistance
- You use a non-preferred provider and your policy doesn't allow it
- You've exceeded your towing sub-limit

**Apex Towing Can Invoice Insurers Directly**
For accident-related towing, we can invoice major insurers directly. Keep your policy number handy when you call us. This saves you paying upfront and waiting for reimbursement.

Bottom line: read your PDS, know your coverage, and save our number before you need it.`,
  },
  {
    title: 'Why Your Old Car Is Worth More Than You Think',
    category: 'Cash for Cars',
    excerpt: 'Most Brisbane residents dramatically underestimate what their old, damaged, or non-running car is worth. Here\'s what actually drives the value.',
    content: `That old Falcon rusting in your driveway? The flood-damaged Camry? The unregistered ute with a blown engine? They're all worth something — often more than you'd expect.

**Metal Prices Drive the Floor Value**
Even completely written-off vehicles have scrap metal value. Steel, aluminium, copper wiring, and other metals all have market prices. A typical small car contains 600–900kg of steel, which has consistent market value.

**Reusable Parts Add Significant Value**
This is where the real money is. Working parts from vehicles are in constant demand by mechanics and panel beaters. A seemingly worthless car might have:
- A working engine worth $500–$2,000
- A gearbox worth $200–$800
- Doors, bonnets, and panels worth $100–$500 each
- A working alternator, starter motor, or radiator worth $50–$300

**Popular Makes Hold Value Even Wrecked**
Toyota, Mazda, Subaru, and Mitsubishi parts are particularly in demand because millions of these cars are on Australian roads. A 2005 Toyota Hilux with a blown head gasket? Still worth good money in parts.

**What REDUCES the Value**
- Severe fire damage
- Heavy rust throughout the chassis
- Stripped interior with nothing left
- Extremely old vehicles (pre-1990) where parts aren't in demand
- Very rare foreign imports with no parts demand locally

**Get an Honest Assessment**
At Apex Towing, we assess each vehicle fairly based on its actual parts and metal value. We don't pay token amounts — we pay what the vehicle is genuinely worth to us.

Get a free quote today. You might be pleasantly surprised what your old car is worth.`,
  },
  {
    title: 'B2B Towing for Brisbane Auto Yards and Dealerships',
    category: 'Industry & Business',
    excerpt: 'Auto yards, dealerships, and mechanics across Brisbane need a reliable towing partner. Here\'s why businesses choose Apex Towing for their fleet needs.',
    content: `For Brisbane automotive businesses — wreckers, dealerships, mechanics, and fleet operators — towing is an ongoing operational need. Ad hoc towing at retail rates gets expensive fast. There's a better way.

**What B2B Towing Looks Like**
Business towing agreements mean you have a dedicated, reliable partner with:
- Pre-negotiated rates per job
- Priority scheduling over walk-in customers
- Monthly or weekly consolidated invoicing (no more chasing individual receipts)
- Account management and job history tracking
- Direct contact with our operations team

**Who Uses Our B2B Service**

*Auto Wreckers and Dismantlers*
Need vehicles picked up from across Brisbane and delivered to your yard? We handle runs from private sellers, insurance companies, and other dealers. Volume discounts apply.

*Dealerships*
Transporting pre-purchase vehicles, moving trade-ins, delivering to customers. We understand the need for careful handling and timely delivery.

*Mechanics and Repairers*
When a customer's car can't be driven to your workshop, we collect and deliver. We also transport vehicles to specialist repairers when needed.

*Fleet Operators*
Companies running vehicle fleets need reliable breakdown response. We can be set up as your preferred breakdown provider, billed directly against your fleet account.

**Why Reliability Matters for Your Business**
In the automotive trade, time is money. A car sitting un-collected means delayed revenue. We commit to same-day collection for established business accounts when booked before noon.

Ready to discuss a B2B arrangement? Call us or ask about our business account setup — we'll have you running within 24 hours.`,
  },
  {
    title: 'The Hidden Costs of Keeping a Dead Car',
    category: 'Tips & Advice',
    excerpt: 'That old car sitting in your driveway or garage might be costing you more than you realise. Let\'s add up the real numbers.',
    content: `Most Brisbane homeowners with an old, non-running car think it's "just sitting there" at no cost. The reality is different — and the numbers might surprise you.

**Council Rates and Property Value**
If your car is taking up garage space, you're potentially losing thousands in usable square footage. A Brisbane garage is worth approximately $15,000–$30,000 in property value. An unregistered car in the driveway can also reduce street appeal — relevant if you're thinking of selling.

**The Registration Trap**
Many people forget to cancel the registration on a non-running vehicle. They continue paying rego for a car they can't drive. In Queensland, you can submit a Statutory Declaration to cancel rego and claim a refund of unused months.

**Insurance You're Still Paying**
Even if you're not driving the car, you may still be paying comprehensive insurance. Check your policy — if the car isn't being driven, you may be able to switch to third party property only, or cancel entirely.

**Neighbourhood and Council Issues**
An abandoned-looking vehicle can attract council complaints. Brisbane City Council can issue notices about vehicles that appear abandoned, even on private property. Resolving this can be stressful and time-consuming.

**Environmental Concerns**
Old cars leak. Brake fluid, transmission oil, and coolant seep into driveways and potentially into stormwater. This is an environmental and safety concern — and potentially a liability.

**The Solution Is Free**
Free car removal costs you nothing. We come to you, handle all paperwork, and your driveway problem disappears within hours. If the car has any value, we'll pay you cash on the spot.

Stop paying to keep a car that's costing you — call Apex Towing for free removal today.`,
  },
  {
    title: 'How Tow Truck Drivers Assess Your Vehicle on Arrival',
    category: 'Behind the Scenes',
    excerpt: 'Ever wondered what actually happens when a tow truck driver arrives? Here\'s an inside look at how professionals assess and handle your vehicle.',
    content: `When an Apex Towing driver arrives at your location, there's a professional process that happens in the first few minutes. Here's what goes on behind the scenes.

**Initial Safety Assessment**
Before anything else, our driver assesses the scene for safety. This includes checking for fuel leaks, unstable vehicles, traffic risk, and whether bystanders are at a safe distance. Safety always comes first.

**Vehicle Condition Check**
Our driver does a walk-around inspection of your vehicle, checking:
- All four wheels and tyres (especially important for loading)
- Whether the car is in park/neutral
- Handbrake status
- Any obvious fluid leaks
- Body damage (documented for our records)
- Towing points and chassis integrity

**Choosing the Right Load Position**
Different vehicles need to be loaded differently. Front-wheel-drive cars are typically loaded from the front. Rear-wheel-drive from the rear. All-wheel-drive vehicles require special attention to avoid drivetrain damage. Our drivers know these specifics for hundreds of vehicle models.

**Securing the Vehicle**
Once loaded onto the tilt tray, the vehicle is secured with rated tie-down straps at four points. Our drivers carry multiple strap configurations for different vehicle types. Nothing moves — your car is completely safe.

**Documentation**
Our driver records the condition of the vehicle before transport. This protects both you and us. Any pre-existing damage is noted before we move.

**The Drive to Destination**
We drive smoothly and carefully. We avoid sudden braking and sharp corners when carrying your vehicle. Your car's safety during transit is our priority.

**Questions Welcome**
Our drivers are experienced professionals. If you're curious about the process, just ask — we're happy to explain exactly what we're doing and why.`,
  },
  {
    title: 'New Car Smell vs Old Car Reality: When Is It Time to Let Go?',
    category: 'Tips & Advice',
    excerpt: 'Emotional attachment to old cars is real — but at some point, holding on costs more than moving on. Here\'s how to decide when it\'s time.',
    content: `Brisbane mechanics have a rule of thumb: when repair costs exceed the car's value, it's time to consider moving on. But many car owners struggle with this decision for emotional and financial reasons. Let's work through it objectively.

**The 50% Rule**
If a single repair would cost more than 50% of the car's current market value, most financial advisors say it's not worth it. Example: a 2007 Corolla worth $4,000 needing a $2,500 gearbox — borderline at best. If that same car also needs tyres, brakes, and a service soon after? Let it go.

**The Ongoing Maintenance Trap**
Some older vehicles become a constant drain — something new every month. Add up everything you've spent on it in the last 12 months. If that number is higher than 20% of the car's value annually, you're in a money pit.

**Emotional vs Financial Value**
We understand sentimental attachment. Your first car, your late grandparent's vehicle, a car that's been in the family for years — these have genuine emotional value. But there's a point where sentiment becomes financially harmful.

**The Opportunity Cost**
Every dollar spent keeping an old unreliable car running is a dollar not going toward a newer, safer, more reliable vehicle. The money saved in repairs, plus cash received from removal, could be a meaningful deposit on something better.

**Signs It's Genuinely Time**
- The car regularly leaves you stranded
- You're embarrassed or stressed driving it
- Repair quotes keep climbing
- Safety features are years behind modern standards
- It's spent more time parked than driven in the last 6 months

**Make the Call**
Apex Towing offers free removal and cash payment. There's no better time to move on from a car that's become more burden than benefit. Call us for an honest assessment of what your vehicle is worth.`,
  },
  {
    title: 'How to Prepare Your Car for a Long Tow',
    category: 'Tips & Advice',
    excerpt: 'If your car needs to be towed a long distance — from Brisbane to the Sunshine Coast, Gold Coast, or further — a few simple preparations can make the process smoother.',
    content: `Long-distance towing is a different proposition to a standard local job. Whether you're moving interstate, transporting a purchase, or getting a vehicle from a remote location, some preparation ensures everything goes smoothly.

**Before the Tow Truck Arrives**

*Gather Your Keys*
Provide the tow truck driver with all sets of keys. This allows them to put the car in neutral and release the handbrake safely for loading.

*Remove Personal Valuables*
Your GPS, sunglasses, change from the console, documents in the glovebox — all should come out. Items can shift during transport, and it's simply good practice.

*Document the Current Condition*
Take photos of all four sides and the interior before the driver arrives. This gives you a before/after record. Our drivers also document condition on arrival, but having your own photos is wise.

*Check the Fuel Level*
For running vehicles that will be driven off the tilt tray at the destination, make sure there's enough fuel to do so safely.

**Understanding Long-Distance Costs**
Long-distance towing (typically over 50km) is charged per kilometre above the base rate. Ask for a firm quote before agreeing to the job. At Apex Towing, we provide transparent, upfront pricing — no surprises at the destination.

**Queensland to Interstate**
We can transport vehicles to northern New South Wales and back. Interstate towing requires planning around border crossing requirements, especially for unregistered vehicles.

**What We Handle**
All long-distance towing at Apex includes:
- Secure tie-down at 4 points
- Professional loading and unloading
- Regular check-in during long runs
- Delivery condition documentation

Call us for a quote on your long-distance towing need.`,
  },
  {
    title: 'Brisbane Traffic Hotspots Where Breakdowns Happen Most',
    category: 'Local Guide',
    excerpt: 'Some Brisbane roads are more likely to leave you stranded than others. Here\'s where we see the most callouts — and how to prepare.',
    content: `After years of towing across Brisbane, patterns emerge. Certain locations generate a disproportionate share of our callouts. Here's the breakdown.

**M1 Pacific Motorway — The Busiest Road**
Not surprisingly, Brisbane's busiest motorway generates the most breakdown calls. The stretch between Eight Mile Plains and the CBD sees high call volumes, especially during peak hour when overheating is common in slow traffic. Our trucks patrol this corridor regularly.

**Gateway Motorway (M7)**
The Gateway Bridge approach and the industrial sections between Bowen Hills and the airport see significant heavy vehicle breakdowns. Commercial trucks and vans are common callouts here.

**Ipswich Motorway**
The Ipswich Motorway through Richlands, Wacol, and Darra has older road surface and more commercial traffic — a combination that stresses tyres and suspension.

**Coronation Drive and the Western Arterial**
High-traffic commuter routes through inner Brisbane's west see plenty of breakdowns during morning and evening peaks. Limited stopping areas make these stressful situations.

**Suburban Danger Zones**
- Logan Road through Greenslopes and Mount Gravatt (long, hilly, hot)
- Gympie Road through Chermside (constant traffic, aging vehicles in the corridor)
- Sandgate Road through Nundah and Boondall (flat tyres common on rough sections)

**What This Means for You**
If you regularly drive any of these routes, make sure your car is properly maintained. Check tyres monthly, keep coolant topped up, and ensure your battery isn't approaching end-of-life.

And make sure Apex Towing's number is saved in your phone. When you need us, you'll need us quickly.`,
  },
  {
    title: 'Selling Your Unregistered Car in Queensland: Complete Guide',
    category: 'Legal & Safety',
    excerpt: 'Selling or disposing of an unregistered vehicle in Queensland involves specific steps. Get it wrong and you could face fines or ongoing liability.',
    content: `An unregistered vehicle can't be legally driven on Queensland roads, but it can be legally sold or disposed of. Here's exactly how to do it properly.

**Can You Sell an Unregistered Car?**
Yes — you can legally sell an unregistered vehicle in Queensland. The buyer takes it at their own risk. However, you must be transparent that it's unregistered, and the vehicle must be transported (not driven) to the new owner.

**Cancelling the Registration**
If a vehicle is permanently off the road, submit a Statutory Declaration of Non-Use to the Department of Transport and Main Roads. This cancels the registration and may entitle you to a partial refund of unused registration fees.

**The Transfer of Ownership Process**
Even for unregistered vehicles, ownership must be legally transferred. Both buyer and seller must complete the Vehicle Registration Transfer form. Keep a copy — it protects you from future liability.

**Important: Remove Your Plates**
When you sell or dispose of a vehicle, remove your registration plates. Your plates are linked to your identity. If someone uses them illegally after you've sold the car, you could face questions from police.

**What About Cars That Can't Be Driven?**
For non-running unregistered vehicles, your options are:
1. **Sell for cash** — cash-for-car buyers like Apex Towing purchase unregistered vehicles
2. **Free removal** — if the car has no significant value, free removal gets it off your hands
3. **Sell for parts** — advertising individually can yield more, but takes significant time

**What Apex Towing Offers**
We buy unregistered vehicles in any condition across Brisbane. We handle all transfer paperwork on the spot. If the car has value, we pay cash. If not, we remove it for free. Call us for a same-day quote.`,
  },
  {
    title: 'Queensland Summer Car Care: Beat the Brisbane Heat',
    category: 'Maintenance Tips',
    excerpt: 'Brisbane summers are brutal — and they\'re harder on your car than you might realise. Here\'s how to protect your vehicle through the hottest months.',
    content: `Brisbane summers bring extreme heat, intense UV, heavy rainfall, and humidity — all of which accelerate wear on your vehicle. Here's your seasonal car care guide.

**Cooling System First**
Your car's cooling system works overtime in Queensland summer. Before the hot months hit:
- Flush and replace coolant if it's over 2 years old
- Check the radiator cap seal
- Inspect all hoses for cracking or softness
- Ensure the radiator fan is working properly
- Check the serpentine belt that drives the water pump

An overheating car in summer traffic is one of our most common callouts. The fix is almost always preventable.

**Tyres Expand in Heat**
Hot roads and high ambient temperatures increase tyre pressure beyond recommended levels. Check tyre pressure early morning (before the day heats up) monthly during summer. Overinflated tyres are more likely to blowout on hot bitumen.

**Battery Performance**
Contrary to what many think, extreme heat degrades car batteries faster than cold. Brisbane's summer can kill a battery that's three or more years old. Consider a free battery check at any auto parts store.

**Air Conditioning**
If your aircon is underperforming going into summer, get it serviced. A regas typically costs $100–$150 and makes a significant difference to cabin comfort and driver alertness on long drives.

**UV Protection**
Queensland's UV is among the world's most intense. Prolonged sun exposure:
- Fades and cracks interior plastics and leather
- Degrades windscreen rubber seals
- Causes paint oxidation

Park in shade where possible. Use a windscreen sun shade. Consider a ceramic tint on the rear windows for privacy and heat reduction.

**Prepare for Wet Season Too**
The same months bring intense rain and flash flooding. Keep wipers in good condition, check your headlights work, and know your car's flood clearance. Never drive through floodwater — even shallow water can cause engine damage.

Stay safe on Brisbane's roads this summer — and save our number just in case.`,
  },
  {
    title: 'After an Accident: Your Step-by-Step Brisbane Guide',
    category: 'Emergency Tips',
    excerpt: 'The moments after a Brisbane car accident are stressful and confusing. Here\'s a clear, step-by-step guide to protect yourself legally and financially.',
    content: `Car accidents are stressful. Knowing exactly what to do in the moments after dramatically affects your legal protection and insurance outcome. Here's the complete guide.

**Immediate Safety (First 2 Minutes)**
1. Check yourself and passengers for injury
2. If safe to do so, move vehicles out of traffic
3. Hazard lights on
4. Call 000 if anyone is injured — don't delay

**After Safety Is Established**
*Call Police if Required*
In Queensland, you must report an accident to police if:
- Anyone is injured
- The combined damage appears to exceed $10,000
- Any driver is unable to exchange details (unconscious, flees)
- A driver appears impaired

*Exchange Details*
Get from every driver involved:
- Full legal name
- Home address
- Mobile number
- Licence number and state
- Vehicle registration and state
- Insurer name and policy number

**Document Everything**
Before vehicles are moved (if safe), photograph:
- All vehicles from multiple angles
- The collision point on each vehicle
- Road conditions, traffic signals, any relevant signage
- Tyre marks or debris
- The overall scene from a distance

**Don't Admit Fault**
Never say "sorry" or "it was my fault" at the scene. Leave fault determination to insurers and police. Anything you say can be used as evidence.

**Towing After an Accident**
If your car needs towing, you have the right to choose your own operator. Don't feel pressured to use whoever shows up first. Call Apex Towing — we'll come promptly, document the pickup condition, and liaise with your insurer directly.

**After You Get Home**
- Contact your insurer within 24 hours (most policies require this)
- Write down everything you remember while it's fresh
- Keep a copy of all documentation
- Follow up on any injuries — some present hours later

We hope you never need this guide — but it's worth knowing.`,
  },
];

const CATEGORIES = ['Tips & Advice','Emergency Tips','Car Removal','Cash for Cars','Maintenance Tips','Legal & Safety','Industry & Business','Local Guide','Behind the Scenes','Weather & Safety','Seasonal'];
const TONES = [
  { id:'friendly',      label:'Friendly & Conversational',   desc:'Approachable, casual, easy to read'      },
  { id:'professional',  label:'Professional & Authoritative', desc:'Expert tone, trust-building'             },
  { id:'urgent',        label:'Urgent & Action-Focused',      desc:'Drives immediate enquiry or booking'     },
];
const TOPIC_SUGGESTIONS = [
  'What to do when your car won\'t start in Brisbane',
  'Benefits of same-day car removal',
  'How to negotiate cash for old cars',
  'Brisbane\'s worst roads for tyre blowouts',
  'Signs your transmission is failing',
  'Understanding tow truck fees in Queensland',
  'How to avoid roadside scams in Brisbane',
  'Electric vehicle breakdowns: what\'s different',
  'How weather affects your car battery',
  'Tips for driving safely in Brisbane rain',
];

// ── Main Component ─────────────────────────────────────────────────────────
export default function Blog() {
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAI, setShowAI]         = useState(false);
  const [form, setForm]             = useState(blank);
  const [toast, setToast]           = useState('');

  // AI states
  const [aiTopic, setAiTopic]       = useState('');
  const [aiCategory, setAiCategory] = useState('Tips & Advice');
  const [aiTone, setAiTone]         = useState('friendly');
  const [aiLoading, setAiLoading]   = useState(false);
  const [aiError, setAiError]       = useState('');
  const [aiKey, setAiKey]           = useState('');
  const [aiKeyLoaded, setAiKeyLoaded] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/blog'); setPosts(r.data); }
    catch(e){} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load API key from settings
  useEffect(() => {
    api.get('/api/settings/general')
      .then(r => { setAiKey(r.data?.anthropic_api_key || ''); setAiKeyLoaded(true); })
      .catch(() => setAiKeyLoaded(true));
  }, []);

  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const data = { ...form, date: form.date || new Date().toISOString().split('T')[0] };
    if (editing) { await api.put(`/api/blog/${editing}`, data); showToast('✅ Post updated!'); }
    else { await api.post('/api/blog', data); showToast('✅ Post created!'); }
    setShowForm(false); setEditing(null); setForm(blank); load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/api/blog/${id}`);
    showToast('Post deleted'); load();
  };

  const toggle = async (id, published) => {
    await api.put(`/api/blog/${id}`, { published: !published });
    showToast(published ? 'Post unpublished' : '✅ Post published to website!');
    load();
  };

  const edit = (post) => { setForm(post); setEditing(post.id); setShowForm(true); };

  const applyTemplate = (tpl) => {
    setForm({
      ...blank,
      title:    tpl.title,
      excerpt:  tpl.excerpt,
      content:  tpl.content,
      category: tpl.category,
      date:     new Date().toISOString().split('T')[0],
    });
    setShowTemplates(false);
    setEditing(null);
    setShowForm(true);
  };

  // ── AI Generation ────────────────────────────────────────────────────────
  const generateWithAI = async () => {
    if (!aiTopic.trim()) { setAiError('Please enter a topic first.'); return; }
    if (!aiKey) { setAiError('No API key found. Add your Anthropic API key in Settings → AI Blog Generation.'); return; }

    setAiLoading(true); setAiError('');
    try {
      const toneLabel = TONES.find(t => t.id === aiTone)?.label || 'friendly';
      const prompt = `Write a helpful blog post for a Brisbane towing and car removal company website.

Topic: ${aiTopic}
Category: ${aiCategory}
Tone: ${toneLabel}
Location focus: Brisbane, Queensland, Australia

Requirements:
- Title: Engaging, SEO-friendly, specific to Brisbane
- Excerpt: 2-3 sentences summarising the post
- Content: 350-450 words, practical and helpful, use **bold** for section headers
- Include Brisbane-specific details where relevant
- Natural, readable prose (not bullet-heavy)
- End with a subtle call to action for towing services

Return ONLY valid JSON (no markdown code blocks), exactly this format:
{"title":"...","excerpt":"...","content":"...","category":"${aiCategory}"}`;

      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': aiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 1200,
          messages: [{ role: 'user', content: prompt }],
        }),
      });

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error?.message || `API error ${resp.status}`);
      }

      const data  = await resp.json();
      const text  = data.content[0].text;
      const clean = text.replace(/```json|```/g, '').trim();
      const post  = JSON.parse(clean);

      setForm({
        ...blank,
        title:    post.title    || '',
        excerpt:  post.excerpt  || '',
        content:  post.content  || '',
        category: post.category || aiCategory,
        date:     new Date().toISOString().split('T')[0],
      });
      setShowAI(false);
      setEditing(null);
      setShowForm(true);
      showToast('✅ AI post generated — review and publish!');
    } catch(e) {
      setAiError(`Generation failed: ${e.message}. Check your API key in Settings.`);
    } finally {
      setAiLoading(false);
    }
  };

  // Schedule reminder — show if no post published in last 7 days
  const lastPublished = posts.filter(p => p.published).sort((a,b) => new Date(b.date)-new Date(a.date))[0];
  const daysSincePost = lastPublished
    ? Math.floor((Date.now() - new Date(lastPublished.date)) / 86400000)
    : 999;
  const showReminder = daysSincePost >= 7;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Blog</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            <span className="text-green-600 font-bold">{posts.filter(p=>p.published).length} live</span> on website ·
            {posts.filter(p=>!p.published).length} drafts · {posts.length} total
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={() => setShowTemplates(true)}
            className="flex items-center gap-2 border-2 border-apex-blue-200 text-apex-blue-700 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-50">
            <BookOpen className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => { setAiError(''); setShowAI(true); }}
            className="flex items-center gap-2 border-2 border-purple-200 text-purple-700 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-purple-50">
            <Sparkles className="w-4 h-4" /> AI Generate
          </button>
          <button onClick={() => { setForm(blank); setEditing(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-apex-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-apex-blue-600">
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>
      </div>

      {/* Weekly reminder banner */}
      {showReminder && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-amber-600 shrink-0" />
            <div>
              <p className="font-bold text-amber-800 text-sm">
                {daysSincePost >= 999 ? "You haven't published any posts yet!" : `It's been ${daysSincePost} days since your last post`}
              </p>
              <p className="text-amber-700 text-xs mt-0.5">Fresh content helps you rank higher on Google. Post at least weekly for best results.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowTemplates(true)}
              className="bg-amber-600 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-amber-700 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> Pick a Template
            </button>
            <button onClick={() => setShowAI(true)}
              className="bg-purple-600 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-purple-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Generate with AI
            </button>
          </div>
        </div>
      )}

      {/* Post list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {posts.length === 0 && (
            <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-200">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="font-bold text-gray-600 text-lg">No blog posts yet</p>
              <p className="text-sm mt-1 mb-5">Start with a template — 20 Brisbane towing posts ready to publish</p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => setShowTemplates(true)} className="bg-apex-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-apex-blue-600">
                  Browse Templates
                </button>
                <button onClick={() => setShowAI(true)} className="bg-purple-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Generate with AI
                </button>
              </div>
            </div>
          )}
          {posts.map(p => (
            <div key={p.id} className="bg-white rounded-2xl border border-gray-200 flex overflow-hidden hover:shadow-md transition-all">
              {p.image && (
                <img src={p.image} alt={p.title} className="w-28 md:w-40 object-cover shrink-0"
                  onError={e => e.target.style.display='none'} />
              )}
              <div className="flex-1 p-4 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full flex items-center gap-1
                    ${p.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {p.published ? <><Globe className="w-2.5 h-2.5" /> Live</> : 'Draft'}
                  </span>
                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{p.category}</span>
                </div>
                <h3 className="font-black text-gray-900 text-base mb-1 leading-snug">{p.title}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{p.excerpt}</p>
                <p className="text-xs text-gray-400 mt-2">{p.date} · {p.author}</p>
              </div>
              <div className="flex flex-col gap-2 p-3 border-l border-gray-100 justify-center shrink-0">
                <button onClick={() => edit(p)} title="Edit"
                  className="p-2 rounded-xl border border-gray-200 hover:bg-blue-50 text-apex-blue-500"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => toggle(p.id, p.published)} title={p.published ? 'Unpublish' : 'Publish'}
                  className={`p-2 rounded-xl border text-xs font-bold ${p.published ? 'border-gray-200 text-gray-500 hover:bg-gray-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                  {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => del(p.id)} title="Delete"
                  className="p-2 rounded-xl border border-red-200 hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit / New Post Modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) { setShowForm(false); setEditing(null); } }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <h2 className="font-black text-apex-blue-700">{editing ? 'Edit Post' : 'New Post'}</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Fill in and publish when ready</span>
                <button onClick={() => { setShowForm(false); setEditing(null); }}><X className="w-5 h-5 text-gray-400" /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 space-y-4">
              <label className="flex flex-col gap-1.5"><span className="text-xs font-bold text-gray-500 uppercase">Title *</span>
                <input value={form.title} onChange={sf('title')} className={inputCls} placeholder="5 Signs Your Car Needs Towing" /></label>
              <div className="grid grid-cols-3 gap-3">
                <label className="flex flex-col gap-1.5"><span className="text-xs font-bold text-gray-500 uppercase">Category</span>
                  <select value={form.category} onChange={sf('category')} className={`${inputCls} bg-white`}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5"><span className="text-xs font-bold text-gray-500 uppercase">Author</span>
                  <input value={form.author} onChange={sf('author')} className={inputCls} /></label>
                <label className="flex flex-col gap-1.5"><span className="text-xs font-bold text-gray-500 uppercase">Date</span>
                  <input type="date" value={form.date} onChange={sf('date')} className={inputCls} /></label>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase">Feature Image (optional)</span>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 bg-apex-blue-50 border-2 border-dashed border-apex-blue-200 rounded-xl hover:bg-apex-blue-100 transition-all text-sm font-bold text-apex-blue-700">
                    📷 Upload Image
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => setForm(f => ({ ...f, image: ev.target.result }));
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                  {form.image && <button type="button" onClick={() => setForm(f => ({ ...f, image: '' }))}
                    className="text-xs text-red-500 hover:text-red-700 font-bold">✕ Remove</button>}
                </div>
                {form.image && <img src={form.image} alt="" className="w-full rounded-xl h-40 object-cover mt-1" />}
              </div>
              <label className="flex flex-col gap-1.5"><span className="text-xs font-bold text-gray-500 uppercase">Excerpt (shown in listing)</span>
                <textarea value={form.excerpt} onChange={sf('excerpt')} className={`${inputCls} resize-none`} rows={2} placeholder="2-3 sentence summary shown on the blog listing page..." /></label>
              <label className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-500 uppercase">Full Content</span>
                  <span className="text-xs text-gray-400">Use **text** for bold headings</span>
                </div>
                <textarea value={form.content} onChange={sf('content')} className={`${inputCls} resize-none`} rows={12}
                  placeholder="Write your full blog post here. Use **bold text** for section headings..." /></label>
              <label className="flex items-center gap-3 cursor-pointer bg-gray-50 rounded-xl p-3">
                <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} className="w-4 h-4 accent-apex-blue-700" />
                <div>
                  <p className="text-sm font-semibold text-gray-700">Publish immediately</p>
                  <p className="text-xs text-gray-400">Makes this post visible on your website now</p>
                </div>
              </label>
              <div className="flex gap-3">
                <button onClick={async () => {
                  const data = { ...form, published: false, date: form.date || new Date().toISOString().split('T')[0] };
                  if (editing) { await api.put(`/api/blog/${editing}`, data); showToast('Saved as draft'); }
                  else { await api.post('/api/blog', data); showToast('Saved as draft'); }
                  setShowForm(false); setEditing(null); setForm(blank); load();
                }} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-50">
                  Save as Draft
                </button>
                <button onClick={async () => {
                  const data = { ...form, published: true, date: form.date || new Date().toISOString().split('T')[0] };
                  if (editing) { await api.put(`/api/blog/${editing}`, data); showToast('✅ Post published!'); }
                  else { await api.post('/api/blog', data); showToast('✅ Post published!'); }
                  setShowForm(false); setEditing(null); setForm(blank); load();
                }} className="flex-1 bg-apex-blue-700 text-white py-3 rounded-xl font-bold hover:bg-apex-blue-600 flex items-center justify-center gap-2">
                  <Globe className="w-4 h-4" /> {editing ? 'Save & Publish' : 'Create & Publish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Template Picker Modal ── */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setShowTemplates(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
              <div>
                <h2 className="font-black text-apex-blue-700">Brisbane Towing Blog Templates</h2>
                <p className="text-xs text-gray-400 mt-0.5">{TEMPLATES.length} ready-to-publish posts · Click any to pre-fill the editor</p>
              </div>
              <button onClick={() => setShowTemplates(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="overflow-y-auto p-5">
              <div className="grid md:grid-cols-2 gap-3">
                {TEMPLATES.map((tpl, i) => (
                  <button key={i} onClick={() => applyTemplate(tpl)}
                    className="text-left p-4 rounded-2xl border-2 border-gray-200 hover:border-apex-blue-400 hover:bg-apex-blue-50 transition-all group">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 shrink-0">{tpl.category}</span>
                      <span className="text-xs text-apex-blue-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity shrink-0">Use this →</span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm leading-snug mb-1">{tpl.title}</h3>
                    <p className="text-xs text-gray-400 line-clamp-2">{tpl.excerpt}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Generator Modal ── */}
      {showAI && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget && !aiLoading) setShowAI(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="bg-gradient-to-r from-purple-700 to-apex-blue-700 rounded-t-2xl px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">AI Blog Generator</p>
                  <h2 className="text-white font-black text-xl mt-0.5">Write a Post with AI</h2>
                  <p className="text-white/50 text-xs mt-0.5">~$0.001 per post · Powered by Claude AI</p>
                </div>
                {!aiLoading && <button onClick={() => setShowAI(false)} className="bg-white/15 text-white p-2 rounded-lg"><X className="w-4 h-4" /></button>}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {aiLoading ? (
                <TowTruckAnimation message="Writing your blog post..." />
              ) : (
                <>
                  {/* API Key check */}
                  {aiKeyLoaded && !aiKey && (
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                      <p className="text-amber-800 font-bold text-sm mb-1">⚠️ Anthropic API Key Required</p>
                      <p className="text-amber-700 text-xs mb-2">
                        AI generation requires a free Anthropic API key. Sign up at <strong>console.anthropic.com</strong> — free tier gives you enough for hundreds of blog posts.
                      </p>
                      <p className="text-amber-700 text-xs">Then add your key in <strong>Admin → Settings → AI Blog Generation</strong>.</p>
                    </div>
                  )}
                  {aiKeyLoaded && aiKey && (
                    <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700 text-xs font-medium">API key configured · Ready to generate</span>
                    </div>
                  )}

                  {/* Topic */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">What should the post be about? *</label>
                    <input value={aiTopic} onChange={e => setAiTopic(e.target.value)} className={inputCls}
                      placeholder="e.g. Why Brisbane drivers should always keep a tow truck number saved" />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {TOPIC_SUGGESTIONS.slice(0,5).map(t => (
                        <button key={t} onClick={() => setAiTopic(t)}
                          className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full hover:bg-apex-blue-100 hover:text-apex-blue-700 transition-all text-left">
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Category</label>
                    <select value={aiCategory} onChange={e => setAiCategory(e.target.value)} className={`${inputCls} bg-white`}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Tone */}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Writing Tone</label>
                    <div className="space-y-2">
                      {TONES.map(t => (
                        <button key={t.id} onClick={() => setAiTone(t.id)}
                          className={`w-full p-3 rounded-xl border-2 text-left transition-all
                            ${aiTone===t.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <p className={`text-sm font-bold ${aiTone===t.id ? 'text-purple-700' : 'text-gray-700'}`}>{t.label}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {aiError && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs">{aiError}</div>}

                  <button onClick={generateWithAI} disabled={!aiTopic.trim() || !aiKey}
                    className="w-full py-4 rounded-xl font-black text-base flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: aiTopic.trim() && aiKey ? 'linear-gradient(135deg, #7c3aed, #0D2B6B)' : undefined, color:'white', backgroundColor: !aiTopic.trim() || !aiKey ? '#e5e7eb' : undefined }}>
                    <Sparkles className="w-5 h-5" /> Generate Blog Post
                  </button>
                  <p className="text-center text-xs text-gray-400">~5–10 seconds · Review before publishing · ~$0.001 cost</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}